import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import RecruiterLayout from '../components/RecruiterLayout';
import { JOB_BOARDS, BOARD_META } from '../constants/jobBoards';
import {
  CONTINENTS, GLOBAL_BOARDS, boardsForCountry, localBoardCount,
  regionLocalBoardCount, continentLocalBoardCount,
} from '../constants/geoBoards';
import BrandLogos from '../components/BrandLogos';
import {
  Check, Building2, Lock, Clock, Globe, Globe2, MapPin, ChevronRight, ArrowLeft,
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const statusMeta = (board, state) => {
  if (board.scope === 'platform') return { text: 'Available via Jobasti', cls: 'bg-green-50 text-green-700', icon: Check };
  if (board.scope === 'organization') {
    return state === 'connected'
      ? { text: 'Connected', cls: 'bg-green-50 text-green-700', icon: Building2 }
      : { text: 'Connect in Settings', cls: 'bg-amber-50 text-amber-700', icon: Lock };
  }
  return { text: 'Coming soon', cls: 'bg-gray-100 text-gray-500', icon: Clock };
};

const MapCard = ({ title, subtitle, onClick, testid, icon: Icon = Globe2 }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="group relative text-left rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 hover:from-blue-50 hover:to-blue-100 hover:border-blue-300 p-4 transition-all shadow-sm hover:shadow"
  >
    <div className="flex items-start justify-between">
      <span className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-500 group-hover:text-blue-600 transition-colors">
        <Icon className="w-5 h-5" />
      </span>
      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-colors mt-3" />
    </div>
    <h3 className="mt-3 font-semibold text-slate-900 text-sm leading-tight">{title}</h3>
    {subtitle && <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>}
  </button>
);

/** Browse-only geographic explorer: Continent → Region → Country → boards operating there. */
const RecruiterJobBoards = () => {
  const { userProfile } = useAuth();
  const orgId = userProfile?.organizationId;
  const [connectionByKey, setConnectionByKey] = useState({});
  const [level, setLevel] = useState('continents');
  const [activeContinent, setActiveContinent] = useState(null);
  const [activeRegion, setActiveRegion] = useState(null);
  const [activeCountry, setActiveCountry] = useState(null); // { code, name } — 'GLOBAL' = worldwide

  useEffect(() => {
    if (!orgId) return;
    axios
      .get(`${API}/api/job-boards/catalog?organizationId=${orgId}`)
      .then((r) => {
        const map = {};
        (r.data?.data || []).forEach((b) => { map[b.key] = b.connectionState; });
        setConnectionByKey(map);
      })
      .catch(() => setConnectionByKey({}));
  }, [orgId]);

  const goContinents = () => { setLevel('continents'); setActiveContinent(null); setActiveRegion(null); setActiveCountry(null); };
  const goRegions = () => { setLevel('regions'); setActiveRegion(null); setActiveCountry(null); };
  const goCountries = () => { setLevel('countries'); setActiveCountry(null); };

  const currentBoards = () => (activeCountry?.code === 'GLOBAL' ? GLOBAL_BOARDS : boardsForCountry(activeCountry?.code));

  const Crumb = ({ label, onClick, active }) => (
    <button type="button" onClick={onClick} disabled={active} className={`text-sm ${active ? 'text-slate-900 font-semibold cursor-default' : 'text-blue-600 hover:text-blue-700'}`}>{label}</button>
  );

  return (
    <RecruiterLayout title="Job Boards" subtitle="Explore boards by continent, region & country — and how they perform there">
      <div className="max-w-6xl mx-auto p-4 lg:p-6" data-testid="recruiter-jobboards-page">
        {/* Back + breadcrumb */}
        <div className="flex items-center gap-3 mb-5">
          {level !== 'continents' && (
            <button
              onClick={level === 'regions' ? goContinents : level === 'countries' ? goRegions : goCountries}
              className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
              data-testid="jobboards-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          )}
          <div className="flex items-center gap-1.5 flex-wrap text-slate-400" data-testid="jobboards-breadcrumb">
            <Crumb label="World" onClick={goContinents} active={level === 'continents'} />
            {activeContinent && (<><ChevronRight className="w-3.5 h-3.5" /><Crumb label={activeContinent.name} onClick={goRegions} active={level === 'regions'} /></>)}
            {activeRegion && (<><ChevronRight className="w-3.5 h-3.5" /><Crumb label={activeRegion.name} onClick={goCountries} active={level === 'countries'} /></>)}
            {activeCountry && (<><ChevronRight className="w-3.5 h-3.5" /><span className="text-sm text-slate-900 font-semibold">{activeCountry.name}</span></>)}
          </div>
        </div>

        {/* Continents */}
        {level === 'continents' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="jobboards-continents">
            <MapCard
              title="Worldwide"
              subtitle={`${GLOBAL_BOARDS.length} global boards`}
              icon={Globe}
              testid="jobboards-continent-worldwide"
              onClick={() => { setActiveContinent(null); setActiveRegion(null); setActiveCountry({ code: 'GLOBAL', name: 'Worldwide' }); setLevel('boards'); }}
            />
            {CONTINENTS.map((c) => {
              const cnt = continentLocalBoardCount(c);
              return (
                <MapCard
                  key={c.id}
                  title={c.name}
                  subtitle={`${c.regions.length} regions · ${cnt} local board${cnt === 1 ? '' : 's'}`}
                  testid={`jobboards-continent-${c.id}`}
                  onClick={() => { setActiveContinent(c); setLevel('regions'); }}
                />
              );
            })}
          </div>
        )}

        {/* Regions */}
        {level === 'regions' && activeContinent && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="jobboards-regions">
            {activeContinent.regions.map((r) => {
              const cnt = regionLocalBoardCount(r);
              return (
                <MapCard
                  key={r.id}
                  title={r.name}
                  subtitle={`${r.countries.length} countries · ${cnt} local board${cnt === 1 ? '' : 's'}`}
                  icon={MapPin}
                  testid={`jobboards-region-${r.id}`}
                  onClick={() => { setActiveRegion(r); setLevel('countries'); }}
                />
              );
            })}
          </div>
        )}

        {/* Countries */}
        {level === 'countries' && activeRegion && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="jobboards-countries">
            {activeRegion.countries.map((c) => {
              const total = boardsForCountry(c.code).length;
              const local = localBoardCount(c.code);
              return (
                <MapCard
                  key={c.code}
                  title={c.name}
                  subtitle={local > 0 ? `${total} boards (${local} local)` : `${total} global boards`}
                  icon={MapPin}
                  testid={`jobboards-country-${c.code}`}
                  onClick={() => { setActiveCountry(c); setLevel('boards'); }}
                />
              );
            })}
          </div>
        )}

        {/* Boards at country level — listed as a table */}
        {level === 'boards' && activeCountry && (
          <div data-testid="jobboards-boards">
            <p className="text-sm text-slate-500 mb-3">
              Boards operating in <span className="font-semibold text-slate-800">{activeCountry.name}</span> — with how they perform there.
            </p>
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-sm" data-testid="jobboards-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 bg-slate-50 border-b border-slate-200">
                    <th className="py-2.5 px-4 font-semibold">Job Board</th>
                    <th className="py-2.5 px-3 font-semibold">Strong in</th>
                    <th className="py-2.5 px-3 font-semibold">Sectors</th>
                    <th className="py-2.5 px-4 font-semibold text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {currentBoards().map((b) => {
                    const s = statusMeta(b, connectionByKey[b.id]);
                    const Icon = s.icon;
                    const meta = BOARD_META[b.id] || { strongCountries: [], sectors: [] };
                    return (
                      <tr key={b.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 align-top" data-testid={`jobboard-card-${b.id}`}>
                        <td className="py-3 px-4">
                          <div className="flex items-start gap-2.5">
                            {b.inlineGlyph && BrandLogos[b.inlineGlyph] ? (
                              React.createElement(BrandLogos[b.inlineGlyph], { size: 32 })
                            ) : b.logoUrl ? (
                              <img src={b.logoUrl} alt={b.name} className="w-8 h-8 object-contain mt-0.5" loading="lazy" />
                            ) : (
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-xs mt-0.5" style={{ backgroundColor: b.color }}>
                                {b.name.charAt(0)}
                              </div>
                            )}
                            <div className="min-w-0 max-w-xs">
                              <p className="font-semibold text-slate-900 leading-tight">{b.name}</p>
                              <p className="text-[11px] text-slate-400 flex items-center gap-1"><Globe className="w-3 h-3" /> {b.country}</p>
                              <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{b.description}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex flex-wrap gap-1 max-w-[220px]">
                            {meta.strongCountries.length === 0
                              ? <span className="text-[11px] text-slate-300">—</span>
                              : meta.strongCountries.map((c) => (
                                  <span key={c} className="text-[10px] bg-slate-50 border border-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">{c}</span>
                                ))}
                          </div>
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex flex-wrap gap-1 max-w-[240px]">
                            {meta.sectors.length === 0
                              ? <span className="text-[11px] text-slate-300">—</span>
                              : meta.sectors.map((sec) => (
                                  <span key={sec} className="text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded-full">{sec}</span>
                                ))}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${s.cls}`}>
                            <Icon className="w-3 h-3" /> {s.text}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </RecruiterLayout>
  );
};

export default RecruiterJobBoards;
